'use client'

import React, { MouseEvent, useEffect, useState } from 'react'
import classNames from 'classnames'
import {
  useComponentVisible,
  useTranslations,
  useSiteContext,
  useUserSettings,
  useLayout,
} from '@dustin-web/microsite-components/src/hooks'
import { GetContactQuery } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { IconName } from '@dustin-web/microsite-components/src/icon'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import CompanyMenu from './header-company-menu'
import { DropdownItem, DropdownList } from './components/dropdown'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'
import { GetCustomerNavigationQuery } from '@dustin-web/microsite-data/src/graphql/content/generated/graphql-types'
import UserIcon from '@dustin-web/microsite-components/src/icon/generated/user'
import LogInIcon from '@dustin-web/microsite-components/src/icon/generated/log-in'
import UserPlusIcon from '@dustin-web/microsite-components/src/icon/generated/user-plus'
import { Skeleton } from '@dustin-web/microsite-components/src/skeleton'

type MenuItem = {
  iconName?: IconName
  href?: string
  text?: string
  userName?: string
  companyName?: string
  customerNumber?: string
  show: boolean
  dataClick?: string
  action?: (e: any) => void
  hasArrowIcon?: boolean
}
type AccountMenuProps = {
  contactQueryData?: GetContactQuery | null
  contactQueryError?: Error | null
  customerNavigationData?: GetCustomerNavigationQuery
}

export const getNameWithEllipsis = (name: string, isMinSM: boolean) => {
  let characterBreakpoint = 50
  let ellipsisIndex = 15

  if (isMinSM) {
    characterBreakpoint = 80
    ellipsisIndex = 50
  }

  if (name.length < characterBreakpoint) {
    return name
  }

  const partToReplaceWithEllipsis = name.substring(ellipsisIndex, name.length - 10)
  const nameWithEllipsis = name.replace(partToReplaceWithEllipsis, '...')
  return nameWithEllipsis
}

export const AccountMenu = ({
  contactQueryData,
  contactQueryError,
  customerNavigationData,
}: AccountMenuProps) => {
  if (contactQueryError) {
    throw contactQueryError
  }

  const { translate } = useTranslations()
  const { componentRef, isVisible, setIsVisible } = useComponentVisible<HTMLLIElement>(false)
  const { isProxied } = useSiteContext()
  const { twoLetterISOLanguageName } = useUserSettings()
  const { signIn, signOut, userData } = useAuth()
  const logoutUrl = '/account/logout'
  const { responsive } = useLayout()
  const [showSkeleton, setShowSkeleton] = useState(true)

  const contact = contactQueryData?.contact

  const customerNavigation = customerNavigationData?.customerNavigation
  const isAdmin = userData?.allowImpersonateContact ?? false
  const isPunchOut = userData?.isPunchout ?? false

  //stop using connectedContacts when tempHasMultipleBusinessAccountsFromQuery is removed
  const tempHasMultipleBusinessAccountsFromQuery = (contact?.connectedContacts?.length ?? 0) > 1
  const switchAccountEnabled =
    userData?.hasMultipleB2BContacts || tempHasMultipleBusinessAccountsFromQuery
  const showSwitchAccountButton = switchAccountEnabled && !isPunchOut

  // Watch for contact data
  useEffect(() => {
    if (contact?.businessCustomer?.companyName || contact?.firstName) {
      setShowSkeleton(false)
    }
  }, [contact])

  const loginHandler = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    DigitalData.accountLogin('header')

    if (!isProxied) {
      signIn({
        provider: 'okta',
        bounceUrl: window.location.origin + '/bounce/login',
        language: twoLetterISOLanguageName,
      })

      return false
    }

    window.location.href = '/login?returnUrl=' + window.location.href
  }

  const logoutHandler = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()

    if (!isProxied) {
      signOut({
        bounceUrl: window.location.origin + '/bounce/logout',
      })
      return false
    }

    window.location.href = logoutUrl
  }

  const signedInMenuItems: MenuItem[] = [
    {
      iconName: 'user',
      href: '/account/overview',
      text: translate('MyAccount'),
      show: !isPunchOut,
      hasArrowIcon: true,
    },
    {
      iconName: 'office-4',
      href: customerNavigation?.landingPageUrl ?? '',
      text: customerNavigation?.customerName ?? '',
      show: !!customerNavigation,
      hasArrowIcon: true,
    },
    {
      userName: `${contact?.firstName} ${contact?.lastName}`,
      companyName: `${contact && contact.businessCustomer?.companyName}`,
      customerNumber: `${contact && contact.customer?.erpIdentifier}`,
      show: true,
    },
    {
      iconName: 'switch-account',
      href: '/login/chooseaccount',
      text: translate('AccountMenu_SwitchAccount'),
      dataClick: 'change-account',
      show: switchAccountEnabled && !isPunchOut,
      hasArrowIcon: true,
    },
    {
      iconName: 'log-out',
      href: logoutUrl,
      text: translate('AccountMenu_LogOut'),
      action: logoutHandler,
      show: true,
    },
  ]

  const gaDataPush = (data: any) => {
    DigitalData.pushSimpleEvent(data)
  }

  return !responsive.isSmallScreen ? (
    <li
      className={classNames('c-header__user-item c-header__dropdown', {
        'c-header__dropdown--active': isVisible,
      })}
      ref={componentRef}>
      {showSkeleton ? (
        <div className="u-pt-8 u-pr-0">
          <Skeleton shade={'light-on-dark-bg'} width={178} height={40}></Skeleton>
        </div>
      ) : (
        <button
          id="@accountBtnId"
          className="c-header__user-btn c-header__dropdown-btn button-stripped u-px-4"
          onClick={(e: any) => {
            e.preventDefault()
            gaDataPush('my-account')
            return isVisible ? setIsVisible(false) : setIsVisible(true)
          }}
          aria-expanded={isVisible ? true : false}>
          <span className="c-header__user-icon">
            <UserIcon name="user" />
          </span>

          <div className="u-relative d-flex flex-column no-mouseflow-dom">
            {isAdmin && (
              <div className="c-header__user-admin c-badge t-purple">
                {translate('AccountMenu_Impersonate')}
              </div>
            )}
            {contact?.firstName && contact?.lastName && (
              <div className="c-header__user-title no-mouseflow-dom">
                {`${contact.firstName} ${contact.lastName}`}
              </div>
            )}
            {contact?.businessCustomer?.companyName && (
              <div className="c-header__user-title c-header__user-title--company no-mouseflow-dom">
                {contact.businessCustomer.companyName}
              </div>
            )}
          </div>
        </button>
      )}

      <div className="c-header__dropdown-box" aria-labelledby="@accountBtnId">
        {contact && (
          <DropdownList>
            {signedInMenuItems
              .filter(i => i.show)
              .map((i, key) => (
                <DropdownItem
                  data-click={i.dataClick}
                  key={key}
                  linkID="dustin-registration-header-link"
                  linkHref={i.href}
                  linkOnClick={i.action}
                  title={i.text}
                  iconName={i.iconName}
                  companyName={i.companyName}
                  customerNumber={i.customerNumber}
                  userName={i.userName}
                  hasArrowIcon={i.hasArrowIcon}
                />
              ))}
          </DropdownList>
        )}
      </div>
    </li>
  ) : (
    <>
      {!contact ? (
        <div className="row xs-gutters align-items-start u-mb-24">
          <div className="col-6 u-h-100">
            <a
              id="dustin-login-header-link"
              data-ga-title="my-account-mobile-login"
              className="c-btn c-btn--block u-space-wrap t-dark-green u-py-4 u-h-100 u-border-none u-mr-0"
              style={{ color: '#CAFBE2' }}
              href="/login"
              onClick={loginHandler}>
              <span className="d-flex align-items-center">
                <LogInIcon name="log-in" className="u-mr-8 i-20" />
                <span>{translate('Header_Login')}</span>
              </span>
            </a>
          </div>
          <div className="col-6 u-h-100">
            <a
              id="dustin-registration-header-link"
              data-ga-title="my-account-mobile-register"
              className="c-btn c-btn--block u-space-wrap u-border-dark-gray u-py-4 u-h-100 u-mr-0 u-color-black"
              href="/registration">
              <span className="d-flex align-items-center">
                <UserPlusIcon name="user-plus" className="u-mr-8 i-20" />
                <span>{translate('Register_SubmitButton')}</span>
              </span>
            </a>
          </div>
        </div>
      ) : (
        <>
          {isAdmin && (
            <div>
              <div className="c-badge t-purple u-mb-8">{translate('AccountMenu_Impersonate')}</div>
            </div>
          )}
          <CompanyMenu />
          <div className="row xs-gutters">
            {showSwitchAccountButton && (
              <div className="col-6">
                <button type="button" className="c-btn c-btn--block u-border-dark-gray u-mb-0">
                  <a style={{ textDecoration: 'none' }} href="/login/chooseaccount">
                    {translate('AccountMenu_SwitchAccount')}
                  </a>
                </button>
              </div>
            )}
            <div
              className={classNames({
                'col-12': !showSwitchAccountButton,
                'col-6': showSwitchAccountButton,
              })}>
              <button
                type="button"
                className={classNames('c-btn c-btn--block u-border-dark-gray u-mb-0')}>
                <a style={{ textDecoration: 'none' }} href="/account/logout">
                  {translate('AccountMenu_LogOut')}
                </a>
              </button>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default AccountMenu
